import React, { useRef } from 'react'
// eslint-disable-next-line
// @ts-ignore
import Textarea from 'react-textarea-autosize'

import { css } from 'design'
import { cn } from 'util/cn'

import { useTextToInput } from './use_text_to_input'

type Props = {
  name?: string
  actualValue: string
  placeholder?: string
  minRows?: number
  maxRows?: number | null
  cols?: number
  keyCodesToBlurOn?: string[]
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
  textareaProps?: React.InputHTMLAttributes<HTMLTextAreaElement>
  wrapper?: React.ElementType
}

export const TextToTextarea = ({
  name,
  placeholder = '',
  minRows = 1,
  maxRows = 3,
  cols,
  actualValue,
  keyCodesToBlurOn = [],
  buttonProps,
  textareaProps,
  wrapper = React.Fragment,
  ...props
}: Props & React.InputHTMLAttributes<HTMLTextAreaElement>) => {
  const buttonRef = useRef(null)
  const inputRef = useRef(null)

  const { className: buttonClassNameFromProps } = buttonProps || {}
  const { className: textareaClassNameFromProps } = textareaProps || {}

  const {
    value,
    inputFocused,
    setFocused,
    handleChange,
    handleKeyDown,
    handleBlur,
  } = useTextToInput({
    actualValue,
    keyCodesToBlurOn,
    inputRef,
    buttonRef,
    ...props,
  })

  const text = value === '' ? placeholder : value
  const Wrapper = wrapper

  if (inputFocused) {
    return (
      <Textarea
        minRows={minRows}
        maxRows={maxRows}
        cols={cols}
        placeholder={placeholder}
        name={name}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        ref={inputRef}
        {...textareaProps}
        className={cn(
          sharedStyles(),
          textAreaStyles(),
          textareaClassNameFromProps,
        )}
      />
    )
  } else {
    return (
      <button
        ref={buttonRef}
        onClick={setFocused}
        title={text}
        {...buttonProps}
        style={{minHeight: `${minRows}ch`}}
        className={cn(
          sharedStyles(),
          buttonStyles(),
          buttonClassNameFromProps,
          !value && placeholderStyles(),
        )}
      >
        <Wrapper>
          {text || <>&nbsp;</>}
          <span className={editIndicatorStyles()}>Edit</span>
        </Wrapper>
      </button>
    )
  }
}

const sharedStyles = css({
  color: '$grey700',
  borderRadius: '$2',
  border: 'none',
  padding: '2px',
  marginLeft: '-2px',
})

const buttonStyles = css({
  background: 'none',
  textAlign: 'left',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  cursor: 'text',

  '&:hover': {
    background: '$grey100',
  },
})

const textAreaStyles = css({
  background: '$grey100',
  resize: 'none',
  outlineOffset: '-2px',
})

const placeholderStyles = css({
  color: '$grey500 !important',
})

const editIndicatorStyles = css({
  fontSize: '$1',
  // Required override for the background-clip on the parent
  background: '$grey300',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginLeft: '$2',
  pointerEvents: 'none',
  '@md': {
    display: 'none',
  },
})
